/**
 * Timesheet — CATS-backed OData V4 adapter (Option A, Phase 3).
 *
 * The seam the factory in `timesheetDataService.ts` selects when
 * `VITE_TS_BACKEND=cats` (the S/4-hosted build). It implements the frozen
 * `TimesheetBackend` contract method-for-method as OData V4 calls to the RAP
 * service Phase 2 published at `VITE_TS_ODATA_BASE`, so the React timesheet UI
 * never changes but its data now lands in standard CATS (CATSDB) with the
 * DCAA / FAR / Part 11 compliance overlay in the five thin `ZTM12_TS_*` tables.
 *
 * Model:
 *   - a time entry  <->  a `TimeSheet` row (WORKDATE, hours, WBS/order/cost
 *     center, LSTAR); a week  <->  the `TimeSheet` set for a PERNR with WORKDATE
 *     in [weekStart, weekStart+7).
 *   - reads are plain GETs; writes are bound static actions on the `TimeSheet`
 *     collection (`createEntry` / `submit` / `approve` / `reject` / `recall` /
 *     `decideSegment`) POSTed with an X-CSRF-Token.
 *   - status: CATS 10/20 -> pending, 30 -> approved, 60 -> posted (see catsMapper).
 *
 * Transport lives in `catsOData.ts`; pure mapping in `catsMapper.ts`; the
 * synchronous reference-data priming in `catsReferenceData.ts`. Do NOT change
 * the frozen interface — TypeScript enforces conformance (`: TimesheetBackend`).
 *
 * Two Phase-2 open items are threaded here and flagged for Phase 4:
 *   - `createEntry` needs a valid CATS data-entry Profile (Phase 2's one gap).
 *     Supplied via `VITE_TS_CATS_PROFILE`; empty until an admin confirms one.
 *   - there is no delete action yet; `deleteEntry` posts a reversing
 *     `createEntry` (negative hours) so the CO posting nets to zero. A true
 *     UI-delete needs a small `Z_M12_CATS_DELETE` backend wrapper (Phase 4).
 *
 * See: docs/architecture/handoffs/PHASE_3_HANDOFF.md and
 *      docs/architecture/handoffs/timesheet-service-interface.md (the freeze).
 */
import type { TsSubmission, TsWorkflowEvent, TsTimeEntry } from '../types/timesheet.types'
import { absenceTypeForChargeCode } from './ptoConfigService'
import type { TimesheetBackend } from './timesheetDataService'
import { normalizePernr } from '../lib/catsPayableRows'
import type { TsEntryHistoryRow, PendingApprovalSegment } from './timesheetSupabaseService'
import { odataList, invokeAction } from './catsOData'
import {
  CATS_ENTITY,
  CATS_ACTION,
  TIMESHEET_SELECT,
  timeSheetRowToEntry,
  segmentRowToPending,
  entryToCreateEntryParams,
  buildWeekFilter,
  odataString,
  type TimeSheetRow,
  type ApprovalSegmentRow,
} from './catsMapper'

/** CATS data-entry profile (Phase 2's open item). Config-supplied; empty until
 *  an admin confirms a valid profile for the target PERNRs. `createEntry` /
 *  `submit` pass it through as `Profile`. */
function catsProfile(): string {
  return (import.meta.env.VITE_TS_CATS_PROFILE as string | undefined)?.trim() ?? ''
}

function actorOf(actorId?: string | null): string {
  return (actorId ?? '').trim()
}

/** Ensure an entry posted to a provisioned PTO leave WBS carries its absence type
 *  (AWART), even when it was created by a plain charge-line pick rather than the
 *  PTO picker — so it lands in CATS as a real leave record. */
function withPtoAbsence(entry: TsTimeEntry): TsTimeEntry {
  if (entry.absenceType) return entry
  const awart = absenceTypeForChargeCode(entry.chargeCodeId)
  return awart ? { ...entry, absenceType: awart } : entry
}

// ─── Read helpers ────────────────────────────────────────────────────────────

async function fetchWeekRows(employeeId: string, weekStart: string): Promise<TimeSheetRow[]> {
  return odataList<TimeSheetRow>(CATS_ENTITY.TimeSheet, {
    filter: buildWeekFilter(employeeId, weekStart),
    select: TIMESHEET_SELECT,
    orderby: 'WorkDate',
  })
}

/** A CATS record counter (12 digits), as opposed to a client `te-…` draft id. */
function isCatsCounter(id: string): boolean {
  return /^\d{12}$/.test(id)
}

/** The live CATS record already sitting in this entry's cell (same employee,
 *  day and charge object), if any. The read view hides superseded and zeroed
 *  records, so a hit is a record the grid would show. */
async function findLiveEntryId(entry: TsTimeEntry): Promise<string | undefined> {
  const rows = await odataList<TimeSheetRow>(CATS_ENTITY.TimeSheet, {
    filter: `Pernr eq ${odataString(entry.employeeId)} and WorkDate eq ${entry.date}`,
    select: TIMESHEET_SELECT,
  })
  return rows
    .map((r) => timeSheetRowToEntry(r, entry.employeeId))
    .find((e) => e.chargeCodeId === entry.chargeCodeId && isCatsCounter(e.id))?.id
}

/** Sum a week's recorded hours for a given charge object (blank = whole week).
 *  Backfills `ApprovalSegment.SegmentHours` (a 0 placeholder in the CDS). */
function sumHoursForChargeObject(rows: TimeSheetRow[], chargeObject: string): number {
  const co = (chargeObject ?? '').trim()
  const matched = co
    ? rows.filter((r) =>
        (r.WbsElement ?? '').trim() === co ||
        (r.InternalOrder ?? '').trim() === co ||
        (r.SenderCostCenter ?? '').trim() === co)
    : rows
  return Number(matched.reduce((s, r) => s + Number(r.RecordedHours ?? 0), 0).toFixed(2))
}

/** Resolve a submission's PERNR + week start (for the approve/reject actions).
 *  Prefers the ApprovalSegment overlay (carries WeekStart); falls back to the
 *  earliest TimeSheet row for the submission. Returns blanks if unknown. */
async function resolveSubmissionKey(submissionId: string): Promise<{ pernr: string; weekStart: string }> {
  try {
    const segs = await odataList<ApprovalSegmentRow>(CATS_ENTITY.ApprovalSegment, {
      filter: `SubmissionId eq ${odataString(submissionId)}`,
      select: ['Pernr', 'WeekStart', 'SubmissionId'],
      top: 1,
    })
    if (segs[0]) return { pernr: (segs[0].Pernr ?? '').trim(), weekStart: (segs[0].WeekStart ?? '').trim() }
  } catch { /* fall through to TimeSheet */ }
  const rows = await odataList<TimeSheetRow>(CATS_ENTITY.TimeSheet, {
    filter: `SubmissionId eq ${odataString(submissionId)}`,
    select: ['Pernr', 'WorkDate', 'SubmissionId'],
    orderby: 'WorkDate',
    top: 1,
  })
  return { pernr: (rows[0]?.Pernr ?? '').trim(), weekStart: (rows[0]?.WorkDate ?? '').trim() }
}

// ─── Adapter ─────────────────────────────────────────────────────────────────

export const timesheetCatsService: TimesheetBackend = {
  // The SAP HR master stays read-only. The app-level settings the Settings page
  // edits (pay class, schedule type, week start) persist to the in-boundary
  // config store instead, keyed on PERNR, and are laid back over the roster at
  // load (catsReferenceData.loadCatsEmployees). Pay class decides whether Time
  // Evaluation prices the worker at all, so a session-only change was not enough.
  async updateEmployee(employeeId, patch) {
    const [{ timesheetDataService }, { saveEmployeeOverride }] = await Promise.all([
      import('./timesheetDataService'),
      import('./catsEmployeeOverrides'),
    ])
    const pernr = timesheetDataService.getEmployee(employeeId)?.pernr ?? employeeId
    await saveEmployeeOverride(pernr, patch)
  },

  // No leave source in the CATS overlay (PTO is modeled as charge codes). The
  // vacation-cover banner tolerates an empty result, same as mock mode.
  async getApprovedLeavesAsync() { return [] },

  async getEntriesForWeek(employeeId, weekStart) {
    const rows = await fetchWeekRows(employeeId, weekStart)
    // Register the charge objects these rows reference into the charge-code cache
    // so every entry resolves to a grid line (and its WBS/order appears in the
    // picker). The WBS-structure catalog does not always carry the exact charge
    // object a CATS record posted against, which otherwise leaves the entry
    // counted only in the daily total ("disappearing"). Best-effort, never blocks.
    try {
      const [{ chargeObjectsFromRows }, { mergeSapChargeObjectCodes }] = await Promise.all([
        import('./catsReferenceData'),
        import('./timesheetDataService'),
      ])
      mergeSapChargeObjectCodes(chargeObjectsFromRows(rows))
    } catch (err) {
      console.warn('[timesheet-cats] charge-object registration failed', err)
    }
    return rows.map((r) => timeSheetRowToEntry(r, employeeId))
  },

  // Append a CATS record (status 20; ReleaseFlag off). NOTE: createEntry is
  // append-only — an in-place edit posts a NEW CATS record. A true update needs
  // BAPI_CATIMESHEETMGR_CHANGE behind an `updateEntry` action (Phase 4). Needs a
  // valid CATS Profile (Phase 2 open item).
  //
  // Idempotency guard: a cell saved before its first post was read back still
  // carries the client `te-…` id, which Z_M12_CATS_POST treats as "new" and
  // INSERTs again — a second live record on the same charge object + day that
  // the grid cell hides but every total counts. So a non-CATS id first resolves
  // against the live records for that day and, when the cell already has one,
  // posts as a CHANGE of that record instead.
  async upsertEntry(entry) {
    const target = withPtoAbsence(entry)
    const id = isCatsCounter(target.id) ? target.id : (await findLiveEntryId(target)) ?? target.id
    await invokeAction(CATS_ENTITY.TimeSheet, CATS_ACTION.createEntry,
      entryToCreateEntryParams({ ...target, id }, { profile: catsProfile(), release: false }))
  },

  // Delete = "clear the cell" as a traceable CATS CHANGE. We post createEntry with
  // hours 0 and the original record's counter (its entry id); Z_M12_CATS_POST
  // recognizes the counter and calls BAPI_CATIMESHEETMGR_CHANGE to change that
  // record to 0 hours. CATS keeps the full audit chain (original -> status 60, a
  // change record with REFCOUNTER pointing back), and the read view hides
  // superseded + zeroed records so the day nets to empty. (A hard delete or a
  // negative reversal are both wrong: the BAPI rejects negative hours, and a hard
  // delete loses traceability.)
  async deleteEntry(entryId, reason) {
    const rows = await odataList<TimeSheetRow>(CATS_ENTITY.TimeSheet, {
      filter: `EntryId eq ${odataString(entryId)}`,
      select: TIMESHEET_SELECT,
      top: 1,
    })
    const row = rows[0]
      ?? (await odataList<TimeSheetRow>(CATS_ENTITY.TimeSheet, {
        filter: `TimeSheetRecord eq ${odataString(entryId)}`,
        select: TIMESHEET_SELECT,
        top: 1,
      }))[0]
    if (!row) throw new Error(`[cats] deleteEntry: no CATS record for entry ${entryId}`)
    const original = timeSheetRowToEntry(row)
    // When clearing already-approved time, stamp the change reason onto the
    // CHANGE-to-0 record's ShortText so the audit chain carries WHY it was voided.
    const withReason = reason && reason.trim() ? { ...original, notes: reason.trim() } : original
    await invokeAction(CATS_ENTITY.TimeSheet, CATS_ACTION.createEntry,
      entryToCreateEntryParams(withReason, { profile: catsProfile(), release: false, hoursOverride: 0 }))
  },

  // Submissions come from the APPROVAL OVERLAY (ztm12_ts_appr / the ApprovalSegment
  // entity), NOT from a SubmissionId on the CATS rows. Two reasons the old
  // CATS-row derivation showed nothing after Submit:
  //   1. `submit` writes the overlay but does not stamp SubmissionId back onto the
  //      already-persisted CATS records, so `SubmissionId ne ''` matched no rows.
  //   2. even if it had, deriveSubmissions used min(WorkDate) as the week start,
  //      which does not equal the schedule-aligned week the grid matches on.
  // The overlay row carries the real SubmissionId, the ALIGNED WeekStart (the
  // `submit` action's WeekStart param), and the live Decision — so the week's
  // status (submitted / approved / rejected) renders and the week locks.
  async getSubmissions(employeeId) {
    return submissionsFromSegments(employeeId ? `Pernr eq ${odataString(employeeId)}` : undefined, employeeId)
  },

  async getAllSubmissions() {
    return submissionsFromSegments(undefined)
  },

  async getSubmissionById(id) {
    const list = await submissionsFromSegments(`SubmissionId eq ${odataString(id)}`)
    return list.find((s) => s.id === id) ?? null
  },

  // Approval-workflow events for a week's submission, reconstructed from the
  // ApprovalSegment overlay (submitted + decided). Best-effort -> [] (the inline
  // tracker then falls back to status + submittedAt), matching mock mode.
  async getSubmissionHistory(employeeId, weekStart) {
    let segs: ApprovalSegmentRow[]
    try {
      segs = await odataList<ApprovalSegmentRow>(CATS_ENTITY.ApprovalSegment, {
        filter: `Pernr eq ${odataString(employeeId)} and WeekStart eq ${weekStart}`,
      })
    } catch { return [] }
    const events: TsWorkflowEvent[] = []
    for (const s of segs) {
      if ((s.SubmittedAt ?? '').trim()) {
        events.push({ event: 'submitted', at: s.SubmittedAt!, actorId: (s.Pernr ?? '').trim() || null, actorName: null })
      }
      const decision = (s.Decision ?? '').trim()
      if ((s.DecidedAt ?? '').trim() && decision && decision !== 'pending') {
        events.push({ event: decision, at: s.DecidedAt!, actorId: (s.DecidedBy ?? '').trim() || null, actorName: null })
      }
    }
    return events.sort((a, b) => a.at.localeCompare(b.at))
  },

  // No per-entry audit-row entity is exposed (Attestation / ESignature are
  // submission-level evidence). Return [] like mock mode; richer entry history
  // is a later phase.
  async getHistoryForEntries(): Promise<TsEntryHistoryRow[]> { return [] },

  // Manager-mode queue: submissions with a pending ApprovalSegment routed to
  // this approver. Synthesized into TsSubmissions (one per submission).
  async getPendingApprovals(supervisorId) {
    const segs = await odataList<ApprovalSegmentRow>(CATS_ENTITY.ApprovalSegment, {
      filter: pendingForApproverFilter(supervisorId),
    })
    const bySubmission = new Map<string, ApprovalSegmentRow>()
    for (const s of segs) {
      const sid = (s.SubmissionId ?? '').trim()
      if (sid && !bySubmission.has(sid)) bySubmission.set(sid, s)
    }
    const weekCache = new Map<string, TimeSheetRow[]>()
    const out: TsSubmission[] = []
    for (const [sid, s] of bySubmission) {
      const pernr = (s.Pernr ?? '').trim()
      const weekStart = (s.WeekStart ?? '').trim()
      const cacheKey = `${pernr}|${weekStart}`
      let rows = weekCache.get(cacheKey)
      if (!rows) { rows = pernr && weekStart ? await fetchWeekRows(pernr, weekStart) : []; weekCache.set(cacheKey, rows) }
      out.push({
        id: sid,
        employeeId: pernr,
        weekStart,
        totalHours: Number(rows.reduce((sum, r) => sum + Number(r.RecordedHours ?? 0), 0).toFixed(2)),
        status: 'pending',
        submittedAt: (s.SubmittedAt ?? '').trim() || `${weekStart}T00:00:00Z`,
        anomalyCount: 0,
      })
    }
    return out.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))
  },

  async approveSubmission(submissionId, actorId) {
    const { pernr, weekStart } = await resolveSubmissionKey(submissionId)
    await invokeAction(CATS_ENTITY.TimeSheet, CATS_ACTION.approve, {
      Pernr: pernr,
      WeekStart: weekStart || null,
      Profile: catsProfile(),
      SubmissionId: submissionId,
      ReasonCode: '',
      Actor: actorOf(actorId),
    })
  },

  async rejectSubmission(submissionId, actorId, reasonCode) {
    const { pernr, weekStart } = await resolveSubmissionKey(submissionId)
    await invokeAction(CATS_ENTITY.TimeSheet, CATS_ACTION.reject, {
      Pernr: pernr,
      WeekStart: weekStart || null,
      Profile: catsProfile(),
      SubmissionId: submissionId,
      ReasonCode: (reasonCode ?? '').trim(),
      Actor: actorOf(actorId),
    })
  },

  // Persist a week and release it: createEntry for any not-yet-posted draft rows
  // + the `submit` action (release 20->30 + attestation / e-sign / approval-segment
  // overlay). Done as SEQUENTIAL action POSTs, not an OData `$batch` — the V4
  // gateway on this kernel rejects JSON `$batch` with 415 (unsupported media type),
  // and in this app every cell is already persisted on edit, so there is normally
  // nothing to create anyway. Returns a synthesized TsSubmission (no server entity).
  async submitWeek(employeeId, weekStart, _orgId) {
    const rows = await fetchWeekRows(employeeId, weekStart)
    const entries = rows.map((r) => timeSheetRowToEntry(r, employeeId))
    const submissionId = deriveSubmissionId(employeeId, weekStart)
    const profile = catsProfile()

    // Rows read back from CATS carry source 'import' — they already exist, so we
    // never re-create them. Only genuine app drafts (not from a read) are posted.
    for (const e of entries.filter((e) => e.source !== 'import' && !/^\d{12}$/.test(e.id))) {
      await invokeAction(CATS_ENTITY.TimeSheet, CATS_ACTION.createEntry,
        entryToCreateEntryParams(withPtoAbsence({ ...e, submissionId }), { profile, release: false }))
    }
    await invokeAction(CATS_ENTITY.TimeSheet, CATS_ACTION.submit, {
      Pernr: employeeId,
      WeekStart: weekStart || null,
      Profile: profile,
      SubmissionId: submissionId,
      ReasonCode: '',
      Actor: '',
    })

    const totalHours = Number(entries.reduce((s, e) => s + e.hours, 0).toFixed(2))
    return {
      id: submissionId,
      employeeId,
      weekStart,
      totalHours,
      status: 'pending',
      submittedAt: new Date().toISOString(),
      anomalyCount: entries.filter((e) => e.hours > 10).length,
    }
  },

  // Charge-object approval segments (Time Approval Routing). SegmentHours is a 0
  // placeholder in the CDS by design; compute it client-side by summing the
  // approver's week hours for the segment's charge object (handoff §3).
  async getPendingApprovalSegments(approverId) {
    const segs = await odataList<ApprovalSegmentRow>(CATS_ENTITY.ApprovalSegment, {
      filter: pendingForApproverFilter(approverId),
    })
    const weekCache = new Map<string, TimeSheetRow[]>()
    const out: PendingApprovalSegment[] = []
    for (const s of segs) {
      const pernr = (s.Pernr ?? '').trim()
      const weekStart = (s.WeekStart ?? '').trim()
      const cacheKey = `${pernr}|${weekStart}`
      let rows = weekCache.get(cacheKey)
      if (!rows) { rows = pernr && weekStart ? await fetchWeekRows(pernr, weekStart) : []; weekCache.set(cacheKey, rows) }
      const hours = Number(s.SegmentHours ?? 0) || sumHoursForChargeObject(rows, s.ChargeObject ?? '')
      out.push(segmentRowToPending(s, hours))
    }
    return out.sort((a, b) => b.submittedAt.localeCompare(a.submittedAt))
  },

  async decideApprovalSegment(segmentId, decision, actorId, reasonCode) {
    await invokeAction(CATS_ENTITY.TimeSheet, CATS_ACTION.decideSegment, {
      SegmentUuid: segmentId,
      Decision: decision,
      Actor: actorOf(actorId),
      ReasonCode: (reasonCode ?? '').trim(),
    })
  },
}

// ─── Local helpers ───────────────────────────────────────────────────────────

/**
 * Approval work waiting on one person: routed to them, OR delegated to them.
 *
 * The approval segment keeps the ORIGINAL approver in ApproverId and records the
 * stand-in in DelegatedTo, so the audit trail still shows whose approval it was.
 * That means filtering on ApproverId alone hides delegated work from the very
 * person who is supposed to action it, which would make delegation look like it
 * did nothing.
 *
 * The PERNR is normalised to NUMC 8 first: the backend stores the canonical
 * zero-padded value, while the TimeSheet entity hands PERNRs back unpadded.
 */
function pendingForApproverFilter(pernr: string): string {
  const p = odataString(normalizePernr(pernr))
  return `(ApproverId eq ${p} or DelegatedTo eq ${p}) and Decision eq 'pending'`
}

/** Build TsSubmissions from the approval overlay (ztm12_ts_appr / ApprovalSegment).
 *  Each Submit inserts an overlay row; approve/reject/recall UPDATE every row for
 *  the submission, so the latest row per SubmissionId reflects the live decision.
 *  Recalled (withdrawn) submissions are dropped so the week reopens for editing.
 *  Hours + anomaly count are summed from the submission's aligned week of CATS
 *  rows (one lean read per distinct week, cached). */
async function submissionsFromSegments(filter: string | undefined, employeeIdOverride?: string): Promise<TsSubmission[]> {
  let segs: ApprovalSegmentRow[]
  try {
    segs = await odataList<ApprovalSegmentRow>(CATS_ENTITY.ApprovalSegment, filter ? { filter } : {})
  } catch {
    return []
  }
  // Latest overlay row per submission id (by submitted-at, lexical on the ISO/
  // timestamp string). Ties keep the first seen.
  const bySid = new Map<string, ApprovalSegmentRow>()
  for (const s of segs) {
    const sid = (s.SubmissionId ?? '').trim()
    if (!sid) continue
    const prev = bySid.get(sid)
    if (!prev || String(s.SubmittedAt ?? '') > String(prev.SubmittedAt ?? '')) bySid.set(sid, s)
  }
  const weekCache = new Map<string, TimeSheetRow[]>()
  const out: TsSubmission[] = []
  for (const [sid, s] of bySid) {
    const decision = (s.Decision ?? '').trim().toLowerCase()
    if (decision === 'recalled') continue // withdrawn -> week reopens, no active submission
    const pernr = (s.Pernr ?? '').trim() || (employeeIdOverride ?? '')
    const weekStart = (s.WeekStart ?? '').trim()
    const cacheKey = `${pernr}|${weekStart}`
    let rows = weekCache.get(cacheKey)
    if (!rows) { rows = pernr && weekStart ? await fetchWeekRows(pernr, weekStart) : []; weekCache.set(cacheKey, rows) }
    const totalHours = Number(rows.reduce((sum, r) => sum + Number(r.RecordedHours ?? 0), 0).toFixed(2))
    out.push({
      id: sid,
      employeeId: pernr,
      weekStart,
      totalHours,
      status: decision === 'approved' ? 'approved' : decision === 'rejected' ? 'rejected' : 'pending',
      submittedAt: String(s.SubmittedAt ?? '').trim() || (weekStart ? `${weekStart}T00:00:00Z` : new Date(0).toISOString()),
      anomalyCount: rows.filter((r) => Number(r.RecordedHours ?? 0) > 10).length,
    })
  }
  return out.sort((a, b) => b.weekStart.localeCompare(a.weekStart))
}

/** Deterministic submission id for a (PERNR, weekStart): stable so re-submitting
 *  the same week reuses it. Fits the 36-char `SubmissionId` field. */
function deriveSubmissionId(employeeId: string, weekStart: string): string {
  return `${employeeId}__${weekStart}`.slice(0, 36)
}

// Re-export for the reference-data priming path (used by timesheetDataService in
// the cats build). Keeps all CATS OData wiring reachable from one module.
export { loadCatsChargeObjectCodes, loadCatsEmployees } from './catsReferenceData'
export { catsStatusToApp } from './catsMapper'
